import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { CARDS } from '@lunary/tarot-data'
import type { AdminDeckDto } from '@lunary/api-client'
import { toRoman } from '@lunary/app-core/roman'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import expert from '../../shared/ui/expert.module.css'
import { DropSlot } from './drop-slot'
import { deckErrorText } from './errors'
import { matchCardId } from './match-files'
import { useRemoveCard, useThreeCardTexts, useUploadCard } from './queries'
import { ACCEPT, useFileDrop } from './use-file-drop'
import { useUploadQueue } from './use-upload-queue'
import styles from './decks.module.css'

type Tab = 'major' | 'wands' | 'cups' | 'swords' | 'pentacles'

const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'major', label: 'Старшие · 22' },
  { key: 'wands', label: 'Жезлы · 14' },
  { key: 'cups', label: 'Кубки · 14' },
  { key: 'swords', label: 'Мечи · 14' },
  { key: 'pentacles', label: 'Пентакли · 14' },
]

type Props = { deck: AdminDeckDto; uploadsEnabled: boolean; selected: string | null; onSelect: (cardId: string) => void; extra?: ReactNode }

export function CardsPanel({ deck, uploadsEnabled, selected, onSelect, extra }: Props) {
  const upload = useUploadCard(deck.id)
  const remove = useRemoveCard(deck.id)
  const queue = useUploadQueue((cardId, file) => upload.mutateAsync({ cardId, file }))
  const single = useRef<HTMLInputElement>(null)
  const bulk = useRef<HTMLInputElement>(null)
  const target = useRef<string | null>(null)
  const [tab, setTab] = useState<Tab>('major')
  const [doomed, setDoomed] = useState<string | null>(null)
  const have = new Map(deck.cards.map((c) => [c.card_id, c]))
  const full = deck.kind === 'three_cards'
  const texts = useThreeCardTexts()
  const changed = new Set(full ? (texts.data?.items ?? []).filter((t) => !t.is_default).map((t) => t.card_id) : [])
  const cards = CARDS.filter((c) => (tab === 'major' ? c.arcana === 'major' : c.suit === tab))
  const locked = !uploadsEnabled || queue.busy
  const uploadMany = (files: File[]) => queue.run(files.map((file) => ({ key: matchCardId(file.name), file })))
  const zone = useFileDrop(uploadMany, locked)
  const grid = useFileDrop(uploadMany, locked)

  return (
    <section className={expert.panel} aria-labelledby="deck-cards">
      <div className={expert.panelHead}>
        <h2 id="deck-cards">{full ? 'Карты' : 'Карты колоды'}</h2>
        {full ? (
          <div className={styles.tabs} role="group" aria-label="Группа карт">
            {TABS.map((t) => (
              <button key={t.key} type="button" aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        ) : (
          <span className={styles.sub}>
            {deck.majors_count} из 22 · свои {deck.custom_cards.length}
          </span>
        )}
        <span className={expert.spacer} />
        {queue.progress && (
          <span className={styles.defaultBadge} aria-live="polite">
            ↑ {queue.progress.done} из {queue.progress.total}
          </span>
        )}
      </div>
      <div className={styles.drop} data-over={zone.over} {...zone.bind}>
        <span>
          {zone.over ? (
            <b>Отпустите: {zone.count} файл(ов), карта определится по имени</b>
          ) : (
            <>
              Перетащите файлы сюда или прямо на карту. Имя подсказывает аркан: <code>05.jpg</code>, <code>the-hierophant.jpg</code>
              {full ? <> или <code>cups-12.jpg</code></> : null}.
            </>
          )}
        </span>
        <button type="button" className="secondary" disabled={locked} onClick={() => bulk.current?.click()}>
          {queue.busy ? 'Загружаю…' : 'Выбрать файлы'}
        </button>
        <input
          ref={bulk}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            const files = [...(e.target.files ?? [])]
            e.target.value = ''
            if (files.length > 0) uploadMany(files)
          }}
        />
      </div>
      <input
        ref={single}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          const cardId = target.current
          e.target.value = ''
          if (file && cardId) queue.run([{ key: cardId, file }])
        }}
      />
      <div className={styles.gridWrap} {...grid.bind}>
        {grid.over && (
          <div className={styles.gridOverlay} aria-hidden="true">
            <b>Отпустите {grid.count} файл(ов)</b>
            <span>Карта определится по имени файла. Чтобы положить в конкретный слот, отпустите прямо на карте.</span>
          </div>
        )}
        <div className={styles.cards}>
          {cards.map((c) => {
            const image = have.get(c.id)
            const label = c.arcana === 'major' ? toRoman(c.number) : c.name.split(' ')[0]
            return (
              <DropSlot
                key={c.id}
                name={c.name}
                label={label || '+'}
                thumb={image?.thumb_url}
                empty={!image}
                pressed={selected === c.id}
                state={queue.state.get(c.id)}
                badge={changed.has(c.id) ? <span className={styles.own} title="Текст изменён">изм.</span> : undefined}
                dropDisabled={locked}
                ariaLabel={c.name}
                onClick={() => onSelect(c.id)}
                onFiles={(files) => (files.length === 1 && files[0] ? queue.run([{ key: c.id, file: files[0] }]) : uploadMany(files))}
                tools={
                  <>
                    <button
                      type="button"
                      aria-label={`${c.name}: ${image ? 'заменить картинку' : 'загрузить картинку'}`}
                      title={image ? 'Заменить' : 'Загрузить'}
                      disabled={locked}
                      onClick={() => {
                        target.current = c.id
                        single.current?.click()
                      }}
                    >
                      ↻
                    </button>
                    {image && !deck.enabled && (
                      <button type="button" aria-label={`${c.name}: убрать картинку`} title="Убрать" disabled={remove.isPending} onClick={() => setDoomed(c.id)}>
                        ×
                      </button>
                    )}
                  </>
                }
              />
            )
          })}
          {extra}
        </div>
      </div>
      {queue.report.length > 0 && (
        <ul className={styles.queue} aria-live="polite">
          {queue.report.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      <p className={styles.hint}>
        {full
          ? `Всего ${deck.cards_count} из 78. Набор фиксирован: добавить или удалить карту нельзя, только заменить картинку.`
          : 'Старшие обязательны, свои по желанию.'}{' '}
        Нажмите на карту, чтобы открыть её справа. Наведите на карту: ↻ заменить картинку{deck.enabled ? '' : ', × убрать'}. Файл можно перетащить прямо на карту.
        {deck.enabled ? ' Колода включена: убирать картинки можно после выключения.' : ''}
      </p>
      <ConfirmDialog
        open={doomed !== null}
        title="Убрать картинку?"
        text={`Картинка «${CARDS.find((c) => c.id === doomed)?.name ?? ''}» пропадёт из колоды. Без неё колоду нельзя будет включить.`}
        confirmLabel="Убрать"
        danger
        pending={remove.isPending}
        error={remove.isError ? deckErrorText(remove.error) : null}
        onClose={() => setDoomed(null)}
        onConfirm={() => {
          if (doomed) remove.mutate(doomed, { onSuccess: () => setDoomed(null) })
        }}
      />
    </section>
  )
}
